import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Checkbox,
    IconButton,
    Stack,
    Typography,
} from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { GLOBAL_REQUEST_CONFIGS } from '../../services/generic'
import { useTranslation } from 'react-i18next'
import { useConfigs } from '../../context/configs_context'
import { useThemeMode } from '../../context/theme_mode_context'
import { useEffect, useState } from 'react'
import { LayoutConfigsInterface } from '../LayoutConfigs'

const renderColumn = (rawValue: any, column: any) => {
    let value = rawValue
    for (const property of column.property) {
        if (value && value.hasOwnProperty(property)) value = value[property]
        else {
            value = ''
            break
        }
    }

    value = column.parser ? column.parser(value) : value

    return column.customRender ? column.customRender(rawValue, value) : value
}

export const GenericAccordion = ({
    title,
    filters,
    checkboxFilters,
    columns,
    values,
    identifier,
    selectedValues,
    subValueField,
    subValueIndentifier,
    isRecursive,
    pagination,
    actions,
    withoutPaper,
    withoutSelection,
    hideFilters,
    hideSearch,
    hidePagination,
    search,
    handleChangeSearch,
    handleExecuteAction,
    handleChangeSelectedFilter,
    handleChangeSelectedValues,
    handleClickRow,
    handleClickDetails,
    handleReload,
    handleOpenSelectedItems,
    sx,
}: any) => {
    const { t } = useTranslation()
    const { configs } = useConfigs()
    const { mode } = useThemeMode()
    const [headerBgColor, setHeaderBgColor] = useState(
        mode === 'dark' ? '#4F46E5' : '#3a49e7'
    )
    const [headerTextColor, setHeaderTextColor] = useState('white')

    useEffect(() => {
        let nextBg = mode === 'dark' ? '#4F46E5' : '#3a49e7'
        let nextText = 'white'
        if (configs.layoutConfigs) {
            const layoutConfigs = JSON.parse(
                configs.layoutConfigs
            ) as LayoutConfigsInterface
            if (layoutConfigs.table?.headerBgColor)
                nextBg = layoutConfigs.table.headerBgColor
            if (layoutConfigs.table?.headerTextColor)
                nextText = layoutConfigs.table.headerTextColor
        }
        setHeaderBgColor(nextBg)
        setHeaderTextColor(nextText)
    }, [configs, mode])

    return (
        <Box my="12px">
            {values.map((value: any) => {
                const foundSelectedValue =
                    selectedValues?.findIndex(
                        (sv: any) => sv === value[identifier]
                    ) ?? -1

                return (
                    <Accordion
                        defaultExpanded
                        disableGutters
                        sx={{
                            borderRadius: '12px',
                            overflow: 'hidden',
                            border: '1px solid var(--border-color)',
                            boxShadow:
                                '0 1px 3px rgba(16, 24, 40, 0.06), 0 4px 12px rgba(16, 24, 40, 0.04)',
                            mb: '10px',
                            '&:before': { display: 'none' },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={
                                <ArrowDropDownIcon
                                    sx={{ color: headerTextColor }}
                                />
                            }
                            sx={{
                                backgroundColor: headerBgColor,
                                '&.Mui-expanded': {
                                    minHeight: 48,
                                },
                            }}
                        >
                            <Stack
                                direction={'row'}
                                spacing={1}
                                alignItems={'center'}
                                sx={{ minWidth: 0, flex: 1 }}
                            >
                                {!withoutSelection && (
                                    <Checkbox
                                        sx={{
                                            color: headerTextColor,
                                            '&.Mui-checked': {
                                                color: headerTextColor,
                                            },
                                        }}
                                        checked={foundSelectedValue > -1}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            const newSelectedValues = [
                                                ...selectedValues,
                                            ]
                                            if (foundSelectedValue > -1)
                                                newSelectedValues.splice(
                                                    foundSelectedValue,
                                                    1
                                                )
                                            else
                                                newSelectedValues.push(
                                                    value[identifier]
                                                )

                                            handleChangeSelectedValues(
                                                newSelectedValues
                                            )
                                        }}
                                    />
                                )}
                                <Typography
                                    sx={{
                                        color: headerTextColor,
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {renderColumn(value, columns[0])}
                                </Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack
                                direction={'row'}
                                flexWrap={'wrap'}
                                justifyContent={'space-between'}
                                sx={{ rowGap: '12px' }}
                            >
                                {columns.slice(1).map((column: any) => (
                                    <Stack mt={'4px'} width={'50%'}>
                                        <Typography
                                            fontWeight={600}
                                            fontSize={'12px'}
                                            color="text.secondary"
                                            textTransform="uppercase"
                                        >
                                            {column.name}
                                        </Typography>
                                        <Typography fontSize={'14px'}>
                                            {renderColumn(value, column)}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                            {handleClickDetails && (
                                <Typography
                                    color="primary"
                                    sx={{
                                        textDecoration: 'underline',
                                        mt: '12px',
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() =>
                                        handleClickDetails(value[identifier])
                                    }
                                >
                                    {t('Visualizar Detalhes')}
                                </Typography>
                            )}
                        </AccordionDetails>
                    </Accordion>
                )
            })}

            {pagination && (
                <Accordion
                    disableGutters
                    sx={{ boxShadow: 'none', border: 'none' }}
                >
                    <AccordionSummary expandIcon={<></>}>
                        <Stack
                            direction="row"
                            width="100%"
                            alignItems={'center'}
                        >
                            {selectedValues.length > 0 && (
                                <Stack
                                    direction={'row'}
                                    alignItems={'center'}
                                    spacing={1}
                                >
                                    <Typography
                                        fontSize={'13px'}
                                        color="text.secondary"
                                    >
                                        {selectedValues.length} rows selected
                                    </Typography>
                                    <Button
                                        size="small"
                                        variant="text"
                                        color="primary"
                                        startIcon={
                                            <VisibilityIcon fontSize="small" />
                                        }
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '12px',
                                        }}
                                        onClick={handleOpenSelectedItems}
                                    >
                                        {t('Ver seleccionados')}
                                    </Button>
                                </Stack>
                            )}
                            <Stack
                                direction={'row'}
                                alignItems={'center'}
                                justifyContent={'end'}
                                flex={1}
                                spacing={1}
                            >
                                {pagination.rowsCount && (
                                    <Typography
                                        fontSize={'13px'}
                                        color="text.secondary"
                                    >
                                        {(pagination.page - 1) *
                                            GLOBAL_REQUEST_CONFIGS.rows +
                                            1}{' '}
                                        -{' '}
                                        {pagination.page *
                                            GLOBAL_REQUEST_CONFIGS.rows <
                                        pagination.rowsCount
                                            ? pagination.page *
                                              GLOBAL_REQUEST_CONFIGS.rows
                                            : pagination.rowsCount}{' '}
                                        of {pagination.rowsCount}
                                    </Typography>
                                )}
                                <Stack direction={'row'} alignItems={'center'}>
                                    <IconButton
                                        size="small"
                                        disabled={pagination.page === 1}
                                        onClick={() =>
                                            pagination.handleChangePagination(
                                                pagination.page - 1,
                                                GLOBAL_REQUEST_CONFIGS.rows
                                            )
                                        }
                                    >
                                        <KeyboardArrowLeftIcon />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        disabled={
                                            pagination.rowsCount
                                                ? (pagination.page - 1) *
                                                      GLOBAL_REQUEST_CONFIGS.rows +
                                                      values.length >=
                                                  pagination.rowsCount
                                                : values.length !==
                                                  GLOBAL_REQUEST_CONFIGS.rows
                                        }
                                        onClick={() => {
                                            pagination.handleChangePagination(
                                                pagination.page + 1,
                                                GLOBAL_REQUEST_CONFIGS.rows
                                            )
                                        }}
                                    >
                                        <KeyboardArrowRightIcon />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Stack>
                    </AccordionSummary>
                </Accordion>
            )}
        </Box>
    )
}
